
import { useState, useCallback } from 'react';
import { Flight } from '@/types/flight';
import { getFlights } from '@/services/flightService';
import { useToast } from '@/components/ui/use-toast';
import { FlightSearchCriteria } from './FlightSearchFilters';
import { toZonedTime } from 'date-fns-tz';
import { startOfDay, endOfDay, isWithinInterval } from 'date-fns';

export function useFlightSearch() {
  const [searchResults, setSearchResults] = useState<Flight[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { toast } = useToast();

  const searchFlights = useCallback(async (criteria: FlightSearchCriteria) => {
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      console.log('Searching flights with criteria:', criteria);
      
      // Get all flights first, then filter client-side
      // This approach works with the current getFlights implementation
      const allFlights = await getFlights();
      
      // Get current date in PNG timezone (Pacific/Port_Moresby, UTC+10)
      const PNG_TIMEZONE = 'Pacific/Port_Moresby';
      const nowPNG = toZonedTime(new Date(), PNG_TIMEZONE);
      const todayStartPNG = startOfDay(nowPNG);
      const todayEndPNG = endOfDay(nowPNG);
      
      // Apply client-side filtering
      const filteredFlights = allFlights.filter(flight => {
        // Filter to only show today's flights (PNG timezone)
        const departureDate = toZonedTime(new Date(flight.scheduled_departure), PNG_TIMEZONE);
        const arrivalDate = toZonedTime(new Date(flight.scheduled_arrival), PNG_TIMEZONE);
        
        const isDepartureToday = isWithinInterval(departureDate, { start: todayStartPNG, end: todayEndPNG });
        const isArrivalToday = isWithinInterval(arrivalDate, { start: todayStartPNG, end: todayEndPNG });
        
        // Only show flights departing or arriving today in PNG timezone
        if (!isDepartureToday && !isArrivalToday) {
          return false;
        }
        
        // Flight number filter
        if (criteria.flightNumber && !flight.flight_number.toLowerCase().includes(criteria.flightNumber.toLowerCase())) {
          return false;
        }
        
        // Departure airport filter
        if (criteria.departureAirport && 
            !flight.departure_city?.code?.toLowerCase().includes(criteria.departureAirport.toLowerCase()) &&
            !flight.departure_city?.name?.toLowerCase().includes(criteria.departureAirport.toLowerCase())) {
          return false;
        }
        
        // Arrival airport filter
        if (criteria.arrivalAirport && 
            !flight.arrival_city?.code?.toLowerCase().includes(criteria.arrivalAirport.toLowerCase()) &&
            !flight.arrival_city?.name?.toLowerCase().includes(criteria.arrivalAirport.toLowerCase())) {
          return false;
        }
        
        // Status filter
        if (criteria.status && criteria.status !== 'all' && flight.status !== criteria.status) {
          return false;
        }
        
        // Date filters
        const flightDate = new Date(flight.scheduled_departure);
        const flightDateString = flightDate.toISOString().split('T')[0];
        
        // Specific flight date
        if (criteria.flightDate && flightDateString !== criteria.flightDate) {
          return false;
        }
        
        // Date range filters
        if (criteria.dateFrom && flightDateString < criteria.dateFrom) {
          return false;
        }
        
        if (criteria.dateTo && flightDateString > criteria.dateTo) {
          return false;
        }
        
        return true;
      });
      
      // Sort by scheduled departure time
      const sortedFlights = filteredFlights.sort((a, b) => 
        new Date(a.scheduled_departure).getTime() - new Date(b.scheduled_departure).getTime()
      );
      
      setSearchResults(sortedFlights);
      
      console.log(`Found ${sortedFlights.length} flights matching criteria`);
      
    } catch (error: any) {
      console.error('Flight search error:', error);
      toast({
        variant: "destructive",
        title: "Search failed",
        description: error.message || "Failed to search flights. Please try again.",
      });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [toast]);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setHasSearched(false);
  }, []);

  return {
    searchResults,
    isSearching,
    hasSearched,
    searchFlights,
    clearSearch
  };
}
